/**
 * Export-Funktionen für Lohnbuchhaltung.
 *
 * Alle Exporte verwenden den Abrechnungszeitraum (21. Vormonat – 20. Monat).
 */

import { addDays, differenceInCalendarDays, format } from 'date-fns';
import { de } from 'date-fns/locale';
import type { DayEntry, CompanySettings, EmploymentType, WageType, MonthlyApproval, HistorySegment } from '../types';
import { EMPLOYMENT_TYPE_LABELS, WAGE_TYPE_LABELS } from '../types';
import {
  getPayrollPeriod,
  getPayrollPeriodsForYear,
  formatDE,
  type PayrollPeriod,
} from './payrollPeriod';

const BOM = '\uFEFF';

// ────────────────────────────────────────────────────────────────
// Anteilige Monatsgehalt-Berechnung (Dreißigstel-Methode)
// ────────────────────────────────────────────────────────────────

/** Ergebnis der anteiligen Monatsgehalt-Berechnung */
export interface ProrataResult {
  /** Anteiliges Gehalt (gerundet auf 2 Dezimalstellen) */
  anteiligesGehalt: number;
  /** Zu vergütende Kalendertage im Abrechnungszeitraum */
  zuVerguetendeTage: number;
  /** Gesamte Kalendertage des Abrechnungszeitraums */
  gesamtTage: number;
  /** Handelt es sich um einen Teilmonat? */
  istTeilmonat: boolean;
}

/**
 * Berechnet das anteilige Monatsgehalt nach der Dreißigstel-Methode.
 *
 * Rechtliche Grundlage: BAG 16.05.2012 – 5 AZR 251/11
 * Formel: Monatsgehalt / 30 × zu vergütende Kalendertage
 *
 * Jeder Monat wird pauschal mit 30 Tagen angesetzt (§ 191 BGB).
 * Nur relevant für wageType = FIXED.
 *
 * @param monthlySalary - Vertraglich vereinbartes Monatsgehalt
 * @param periodStart - Erster Tag des Abrechnungszeitraums (21. Vormonat)
 * @param periodEnd - Letzter Tag des Abrechnungszeitraums (20. aktueller Monat)
 * @param employmentStart - Beschäftigungsbeginn (null = unbegrenzt)
 * @param employmentEnd - Beschäftigungsende (null = unbegrenzt)
 */
export function calculateProrataMonthly(
  monthlySalary: number,
  periodStart: Date,
  periodEnd: Date,
  employmentStart: Date | null,
  employmentEnd: Date | null,
): ProrataResult {
  // Effektiver Zeitraum = Schnittmenge aus Abrechnungszeitraum und Beschäftigungszeitraum
  const effektivStart = employmentStart && employmentStart > periodStart ? employmentStart : periodStart;
  const effektivEnde = employmentEnd && employmentEnd < periodEnd ? employmentEnd : periodEnd;

  // Gesamte Kalendertage des Abrechnungszeitraums (DST-sicher via date-fns).
  const gesamtTage = differenceInCalendarDays(periodEnd, periodStart) + 1;

  // Zu vergütende Kalendertage.
  const zuVerguetendeTage = Math.max(
    0,
    differenceInCalendarDays(effektivEnde, effektivStart) + 1,
  );

  // Voller Monat → volles Gehalt (keine Kürzung)
  if (zuVerguetendeTage >= gesamtTage) {
    return {
      anteiligesGehalt: monthlySalary,
      zuVerguetendeTage: gesamtTage,
      gesamtTage,
      istTeilmonat: false,
    };
  }

  // Teilmonat → Dreißigstel-Methode (BAG 5 AZR 251/11)
  const anteiligesGehalt = Math.round((monthlySalary / 30) * zuVerguetendeTage * 100) / 100;

  return {
    anteiligesGehalt,
    zuVerguetendeTage,
    gesamtTage,
    istTeilmonat: true,
  };
}

/** Aggregierte Daten für einen Abrechnungszeitraum */
export interface PayrollMonthData {
  period: PayrollPeriod;
  /** LA 1000: Reine Arbeitsstunden (ohne Krank/Urlaub/Feiertag) */
  workedHours: number;
  /** LA 1650: Krankheitsstunden */
  sickHours: number;
  /** LA 1600: Urlaubsstunden */
  vacationHours: number;
  /** LA 1012: Feiertagsstunden (nur Feiertage auf Arbeitstage) */
  holidayHours: number;
  /** Summe aller Stundenkategorien */
  totalHours: number;
  /** LA 8560: Anzahl Urlaubstage */
  vacationDays: number;
}

/**
 * Berechnet die Lohnabrechnungsdaten für einen Abrechnungszeitraum.
 */
export function calculatePayrollMonth(
  entries: Record<string, DayEntry>,
  period: PayrollPeriod,
  settings: CompanySettings
): PayrollMonthData {
  let workedHours = 0;
  let sickHours = 0;
  let vacationHours = 0;
  let holidayHours = 0;
  let vacationDays = 0;

  // Alle Tage im Abrechnungszeitraum durchgehen (addDays ist DST-sicher).
  let current = new Date(period.start);
  while (current <= period.end) {
    const dateStr = format(current, 'yyyy-MM-dd');
    const entry = entries[dateStr];
    const dayOfWeek = current.getDay(); // 0=So, 6=Sa
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    if (entry && !isWeekend) {
      if (entry.isSickDay) {
        sickHours += entry.hours;
      } else if (entry.isVacation) {
        vacationHours += entry.hours;
        vacationDays++;
      } else if (entry.isHalfVacation) {
        vacationHours += entry.hours;
        vacationDays += 0.5;
      } else if (entry.isHoliday) {
        // Feiertag auf Arbeitstag: Sollstunden
        holidayHours += settings.hoursPerDayFullTime;
      } else if (entry.hours > 0) {
        workedHours += entry.hours;
      }
    }

    current = addDays(current, 1);
  }

  return {
    period,
    workedHours,
    sickHours,
    vacationHours,
    holidayHours,
    totalHours: workedHours + sickHours + vacationHours + holidayHours,
    vacationDays,
  };
}

/**
 * Berechnet Lohnabrechnungsdaten für alle 12 Monate eines Jahres.
 */
export function calculatePayrollYear(
  entries: Record<string, DayEntry>,
  year: number,
  settings: CompanySettings
): PayrollMonthData[] {
  const periods = getPayrollPeriodsForYear(year);
  return periods.map((period) => calculatePayrollMonth(entries, period, settings));
}

// ────────────────────────────────────────────────────────────────
// CSV-Exporte
// ────────────────────────────────────────────────────────────────

const CSV_HEADER = [
  'Name',
  'Monat',
  'Abrechnungszeitraum',
  'Gearbeitete Std (LA 1000)',
  'Krank Std (LA 1650)',
  'Urlaub Std (LA 1600)',
  'Feiertag Std (LA 1012)',
  'Gesamt Std',
  'Urlaubstage (LA 8560)',
].join(';');

function payrollRowToCSV(data: PayrollMonthData, userName: string): string {
  const p = data.period;
  const pad = (n: number) => n.toString().padStart(2, '0');
  const monat = `${pad(p.month)}/${p.year}`;
  const zeitraum = `${format(p.start, 'dd.MM.')}–${format(p.end, 'dd.MM.yyyy')}`;

  return [
    `"${userName}"`,
    monat,
    zeitraum,
    formatDE(data.workedHours),
    formatDE(data.sickHours),
    formatDE(data.vacationHours),
    formatDE(data.holidayHours),
    formatDE(data.totalHours),
    data.vacationDays.toString(),
  ].join(';');
}

/**
 * CSV-Export für einen einzelnen Abrechnungsmonat.
 */
export function exportToPayrollCSV(
  entries: Record<string, DayEntry>,
  year: number,
  month: number,
  settings: CompanySettings,
  userName: string
): void {
  const period = getPayrollPeriod(year, month);
  const data = calculatePayrollMonth(entries, period, settings);
  const content = BOM + CSV_HEADER + '\n' + payrollRowToCSV(data, userName);
  const pad = (n: number) => n.toString().padStart(2, '0');
  downloadFile(content, `stundenliste_lohn_${year}_${pad(month)}.csv`, 'text/csv;charset=utf-8;');
}

/**
 * CSV-Export für alle 12 Abrechnungsmonate + Jahressumme.
 */
export function exportToYearlyCSV(
  entries: Record<string, DayEntry>,
  year: number,
  settings: CompanySettings,
  userName: string
): void {
  const monthlyData = calculatePayrollYear(entries, year, settings);

  const rows = monthlyData.map((d) => payrollRowToCSV(d, userName));

  // Jahressumme
  const totals = monthlyData.reduce(
    (acc, d) => ({
      workedHours: acc.workedHours + d.workedHours,
      sickHours: acc.sickHours + d.sickHours,
      vacationHours: acc.vacationHours + d.vacationHours,
      holidayHours: acc.holidayHours + d.holidayHours,
      totalHours: acc.totalHours + d.totalHours,
      vacationDays: acc.vacationDays + d.vacationDays,
    }),
    { workedHours: 0, sickHours: 0, vacationHours: 0, holidayHours: 0, totalHours: 0, vacationDays: 0 }
  );

  const sumRow = [
    `"${userName}"`,
    `Gesamt ${year}`,
    '',
    formatDE(totals.workedHours),
    formatDE(totals.sickHours),
    formatDE(totals.vacationHours),
    formatDE(totals.holidayHours),
    formatDE(totals.totalHours),
    totals.vacationDays.toString(),
  ].join(';');

  const content = BOM + CSV_HEADER + '\n' + rows.join('\n') + '\n' + sumRow;
  downloadFile(content, `stundenliste_lohn_${year}_gesamt.csv`, 'text/csv;charset=utf-8;');
}

// ────────────────────────────────────────────────────────────────
// Sammel-Export: Alle Mitarbeiter (für ISWL-Vorlage)
// ────────────────────────────────────────────────────────────────

export interface EmployeePayrollInput {
  userId: string;
  userName: string;
  firstName?: string;
  lastName?: string;
  personnelNumber?: string | null;
  employmentType?: EmploymentType | null;
  rv?: boolean;
  hansefit?: boolean;
  contractualHours?: number | null;
  entries: Record<string, DayEntry>;
  settings: CompanySettings;
}

const ALL_EMPLOYEES_HEADER = [
  'Personalnummer',
  'Nachname',
  'Vorname',
  'Beschäftigungsart',
  'RV-pflichtig',
  'Hansefit',
  'Vertragliche Std',
  'Gearbeitete Std (LA 1000)',
  'Krank Std (LA 1650)',
  'Urlaub Std (LA 1600)',
  'Feiertag Std (LA 1012)',
  'Gesamt Std',
  'Urlaubstage (LA 8560)',
].join(';');

/**
 * Sammel-CSV für alle Mitarbeiter — eine Zeile pro Person.
 * Passt 1:1 in die ISWL-Vorlage (Monatstabelle Lohn).
 */
export function exportAllEmployeesPayrollCSV(
  employees: EmployeePayrollInput[],
  year: number,
  month: number
): void {
  const period = getPayrollPeriod(year, month);

  const rows = employees
    .sort((a, b) => (a.lastName ?? a.userName).localeCompare(b.lastName ?? b.userName, 'de'))
    .map((emp) => {
      const data = calculatePayrollMonth(emp.entries, period, emp.settings);
      const empTypeLabel = emp.employmentType ? EMPLOYMENT_TYPE_LABELS[emp.employmentType] : '';
      return [
        `"${emp.personnelNumber ?? ''}"`,
        `"${emp.lastName ?? ''}"`,
        `"${emp.firstName ?? ''}"`,
        `"${empTypeLabel}"`,
        emp.rv ? 'ja' : 'nein',
        emp.hansefit ? 'ja' : 'nein',
        emp.contractualHours != null ? formatDE(emp.contractualHours) : '',
        formatDE(data.workedHours),
        formatDE(data.sickHours),
        formatDE(data.vacationHours),
        formatDE(data.holidayHours),
        formatDE(data.totalHours),
        data.vacationDays.toString(),
      ].join(';');
    });

  const pad = (n: number) => n.toString().padStart(2, '0');
  const content = BOM + `"Abrechnungszeitraum: ${period.label}";;;;;;;;;;;;` + '\n' + ALL_EMPLOYEES_HEADER + '\n' + rows.join('\n');
  downloadFile(content, `stundenliste_lohn_alle_${year}_${pad(month)}.csv`, 'text/csv;charset=utf-8;');
}

/**
 * Tagesdetail-CSV-Export mit Zwischensummen pro Abrechnungszeitraum.
 */
export function exportToDailyCSV(
  entries: Record<string, DayEntry>,
  year: number
): void {
  const header = 'Datum;Wochentag;Arbeitsstunden;Feiertag;Urlaub;Krank;Bemerkung';
  const periods = getPayrollPeriodsForYear(year);
  const lines: string[] = [header];

  for (const period of periods) {
    // Leerzeile + Zeitraum-Header
    lines.push('');
    lines.push(`"${period.label}";;;;;;`);

    let periodHours = 0;
    let periodHolidays = 0;
    let periodVacation = 0;
    let periodSick = 0;

    const current = new Date(period.start);
    while (current <= period.end) {
      const dateStr = format(current, 'yyyy-MM-dd');
      const entry = entries[dateStr];
      const weekday = format(current, 'EEEE', { locale: de });
      const dateFmt = format(current, 'dd.MM.yyyy');

      const hours = entry?.hours ?? 0;
      const isHoliday = entry?.isHoliday ?? false;
      const isVacation = entry?.isVacation ?? false;
      const isHalfVacation = entry?.isHalfVacation ?? false;
      const isSick = entry?.isSickDay ?? false;
      const note = entry?.note ?? '';

      lines.push([
        dateFmt,
        weekday,
        formatDE(hours),
        isHoliday ? 'Ja' : 'Nein',
        isVacation ? 'Ja' : (isHalfVacation ? '½' : 'Nein'),
        isSick ? 'Ja' : 'Nein',
        `"${note.replace(/"/g, '""')}"`,
      ].join(';'));

      periodHours += hours;
      if (isHoliday) periodHolidays++;
      if (isVacation) periodVacation++;
      if (isHalfVacation) periodVacation += 0.5;
      if (isSick) periodSick++;

      current.setDate(current.getDate() + 1);
    }

    // Zwischensumme
    lines.push([
      '"Summe"',
      '',
      formatDE(periodHours),
      periodHolidays.toString(),
      periodVacation.toString(),
      periodSick.toString(),
      '',
    ].join(';'));
  }

  const content = BOM + lines.join('\n');
  downloadFile(content, `stundenliste_tage_${year}.csv`, 'text/csv;charset=utf-8;');
}

// ────────────────────────────────────────────────────────────────
// PDF-Export (dynamisch geladen)
// ────────────────────────────────────────────────────────────────

/**
 * PDF-Jahresbericht mit Zusammenfassung + Monatsdetails.
 * jsPDF wird dynamisch importiert um das Hauptbundle klein zu halten.
 */
export async function exportToPDF(
  entries: Record<string, DayEntry>,
  year: number,
  settings: CompanySettings,
  userName: string,
  results: { totalHours: number; workDays: number; vacationEntitlementRounded: number; vacationDaysTaken: number; remainingVacation: number }
): Promise<void> {
  const [{ jsPDF }, autoTableModule] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);

  // jspdf-autotable registriert sich als Plugin auf jsPDF.prototype
  // Der Import oben reicht — Seiteneffekt
  void autoTableModule;

  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  const DISCLAIMER = 'Diese Auswertung dient ausschließlich als Orientierungshilfe. Die Berechnungen ersetzen keine rechtliche oder steuerliche Beratung.';
  const today = format(new Date(), 'dd.MM.yyyy', { locale: de });

  let totalPages = 0; // wird am Ende gesetzt

  // ── Hilfsfunktionen ──
  function addFooter(pageNum: number) {
    doc.setFontSize(7);
    doc.setTextColor(130);
    doc.text(`Seite ${pageNum}`, margin, pageHeight - 8);
    doc.text(`Erstellt am ${today}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    doc.text('Stundenliste', pageWidth - margin, pageHeight - 8, { align: 'right' });
    doc.setFontSize(6);
    doc.text(DISCLAIMER, pageWidth / 2, pageHeight - 4, { align: 'center', maxWidth: pageWidth - margin * 2 });
  }

  // ── Seite 1: Jahresübersicht ──
  // Header
  doc.setFontSize(16);
  doc.setTextColor(46, 117, 182);
  doc.text('Stundenliste — Arbeitszeit- & Urlaubsverwaltung', margin, 20);
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Jahr ${year} | ${userName} | Erstellt am ${today}`, margin, 28);

  // Zusammenfassung
  doc.setFontSize(12);
  doc.setTextColor(31, 41, 55);
  doc.text('Zusammenfassung', margin, 40);

  (doc as unknown as { autoTable: (options: Record<string, unknown>) => void }).autoTable({
    startY: 44,
    margin: { left: margin, right: margin },
    theme: 'grid',
    headStyles: { fillColor: [46, 117, 182], textColor: 255, fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8 },
    columnStyles: { 0: { cellWidth: 80 }, 1: { halign: 'right' } },
    head: [['Kennzahl', 'Wert']],
    body: [
      ['Gesamtstunden', formatDE(results.totalHours)],
      ['Arbeitstage', results.workDays.toString()],
      ['Urlaubsanspruch (§5 BUrlG)', `${results.vacationEntitlementRounded} Tage`],
      ['Urlaub genommen', `${results.vacationDaysTaken} Tage`],
      ['Resturlaub', `${results.remainingVacation} Tage`],
    ],
  });

  // Monatstabelle
  const monthlyData = calculatePayrollYear(entries, year, settings);

  const summaryY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
  doc.setFontSize(12);
  doc.setTextColor(31, 41, 55);
  doc.text('Monatsübersicht (Abrechnungszeiträume)', margin, summaryY);

  const monthRows = monthlyData.map((d) => [
    d.period.label.split(' (')[0], // Monatsname
    formatDE(d.workedHours),
    formatDE(d.sickHours),
    formatDE(d.vacationHours),
    formatDE(d.holidayHours),
    formatDE(d.totalHours),
    d.vacationDays.toString(),
  ]);

  const totals = monthlyData.reduce(
    (acc, d) => ({
      worked: acc.worked + d.workedHours,
      sick: acc.sick + d.sickHours,
      vacation: acc.vacation + d.vacationHours,
      holiday: acc.holiday + d.holidayHours,
      total: acc.total + d.totalHours,
      vacDays: acc.vacDays + d.vacationDays,
    }),
    { worked: 0, sick: 0, vacation: 0, holiday: 0, total: 0, vacDays: 0 }
  );

  monthRows.push([
    'Gesamt',
    formatDE(totals.worked),
    formatDE(totals.sick),
    formatDE(totals.vacation),
    formatDE(totals.holiday),
    formatDE(totals.total),
    totals.vacDays.toString(),
  ]);

  (doc as unknown as { autoTable: (options: Record<string, unknown>) => void }).autoTable({
    startY: summaryY + 4,
    margin: { left: margin, right: margin },
    theme: 'grid',
    headStyles: { fillColor: [46, 117, 182], textColor: 255, fontStyle: 'bold', fontSize: 7 },
    bodyStyles: { fontSize: 7 },
    head: [['Monat', 'Arbeit', 'Krank', 'Urlaub', 'Feiertag', 'Gesamt', 'UT']],
    body: monthRows,
    didParseCell: (data: { row: { index: number }; cell: { styles: { fontStyle: string; fillColor: number[] } } }) => {
      // Letzte Zeile (Gesamt) fett
      if (data.row.index === monthRows.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [222, 234, 246];
      }
    },
  });

  addFooter(1);

  // ── Seite 2+: Monatsdetails ──
  const periods = getPayrollPeriodsForYear(year);

  for (const period of periods) {
    doc.addPage();
    const pageNum = doc.getNumberOfPages();

    doc.setFontSize(12);
    doc.setTextColor(46, 117, 182);
    doc.text(period.label, margin, 16);

    const dayRows: (string | { content: string; styles: { fillColor?: number[] } })[][] = [];

    const current = new Date(period.start);
    while (current <= period.end) {
      const dateStr = format(current, 'yyyy-MM-dd');
      const entry = entries[dateStr];
      const dayOfWeek = current.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const weekdayShort = format(current, 'EE', { locale: de });
      const dateFmt = format(current, 'dd.MM.');

      const hours = entry?.hours ?? 0;
      let status = '';
      let fillColor: number[] | undefined;

      if (isWeekend) {
        status = 'WE';
        fillColor = [229, 231, 235]; // grau
      } else if (entry?.isHoliday) {
        status = 'Feiertag';
        fillColor = [254, 243, 199]; // gelb
      } else if (entry?.isVacation) {
        status = 'Urlaub';
        fillColor = [191, 219, 254]; // blau
      } else if (entry?.isHalfVacation) {
        status = '½ Urlaub';
        fillColor = [219, 234, 254]; // hellblau
      } else if (entry?.isSickDay) {
        status = 'Krank';
        fillColor = [254, 215, 170]; // orange
      } else if (hours > 0) {
        status = 'Arbeit';
      }

      const note = entry?.note ?? '';

      const rowCells = [dateFmt, weekdayShort, hours > 0 ? formatDE(hours) : '', status, note].map(
        (content) => (fillColor ? { content, styles: { fillColor } } : content)
      );
      dayRows.push(rowCells as (string | { content: string; styles: { fillColor?: number[] } })[]);

      current.setDate(current.getDate() + 1);
    }

    // Monatssumme
    const monthData = calculatePayrollMonth(entries, period, settings);
    dayRows.push([
      { content: 'Summe', styles: { fillColor: [222, 234, 246] } },
      { content: '', styles: { fillColor: [222, 234, 246] } },
      { content: formatDE(monthData.totalHours), styles: { fillColor: [222, 234, 246] } },
      { content: '', styles: { fillColor: [222, 234, 246] } },
      { content: '', styles: { fillColor: [222, 234, 246] } },
    ]);

    (doc as unknown as { autoTable: (options: Record<string, unknown>) => void }).autoTable({
      startY: 22,
      margin: { left: margin, right: margin },
      theme: 'grid',
      headStyles: { fillColor: [46, 117, 182], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      bodyStyles: { fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: 12 },
        2: { cellWidth: 18, halign: 'right' },
        3: { cellWidth: 20 },
      },
      head: [['Tag', 'WT', 'Stunden', 'Status', 'Bemerkung']],
      body: dayRows,
    });

    addFooter(pageNum);
  }

  // Seitenzahlen aktualisieren (Footer bereits gezeichnet, jetzt Total setzen)
  totalPages = doc.getNumberOfPages();
  // Nochmal alle Footers mit Seitenzahl/Gesamtzahl zeichnen
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(130);
    doc.text(`/ ${totalPages}`, margin + doc.getTextWidth(`Seite ${i}`) + 1, pageHeight - 8);
  }

  doc.save(`stundenliste_${year}.pdf`);
}

// ────────────────────────────────────────────────────────────────
// Neue Exporte: Lohnbüro-CSV + GF-CSV
// ────────────────────────────────────────────────────────────────

/** Input-Daten für die neuen Exporte (erweitert um wageType, monthlySalary, approval) */
export interface NewExportEmployeeInput {
  userId: string;
  firstName?: string;
  lastName?: string;
  personnelNumber?: string | null;
  employmentType?: EmploymentType | null;
  department?: string | null;
  rv?: boolean;
  hansefit?: boolean;
  contractualHours?: number | null;
  wageType?: WageType | null;
  monthlySalary?: number | null;
  employmentStart?: string | null;
  employmentEnd?: string | null;
  entries: Record<string, DayEntry>;
  settings: CompanySettings;
  approval?: MonthlyApproval | null;
}

/**
 * Berechnet die abrechenbaren Stunden (Arbeit + Urlaub + Krank) im Abrechnungszeitraum.
 * Exportiert für Tests.
 */
export function calcBillableHours(
  entries: Record<string, DayEntry>,
  period: PayrollPeriod,
): number {
  let total = 0;
  const current = new Date(period.start);
  while (current <= period.end) {
    const dateStr = format(current, 'yyyy-MM-dd');
    const entry = entries[dateStr];
    const dayOfWeek = current.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    if (entry && !isWeekend) {
      if (entry.isVacation || entry.isHalfVacation) {
        total += entry.hours;
      } else if (entry.isSickDay) {
        total += entry.hours;
      } else if (entry.isHoliday) {
        // Feiertage werden nicht als geleistete Stunden gezählt
      } else if (entry.hours > 0) {
        total += entry.hours;
      }
    }

    current.setDate(current.getDate() + 1);
  }
  return total;
}


/** Kurzmonat + 2-stelliges Jahr, z.B. "Feb. 26" */
const SHORT_MONTHS = [
  'Jan.', 'Feb.', 'Mär.', 'Apr.', 'Mai', 'Jun.',
  'Jul.', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.',
];

function formatShortMonth(month: number, year: number): string {
  return `${SHORT_MONTHS[month - 1]} ${String(year).slice(-2)}`;
}

function formatDateDE(isoDate: string | null | undefined): string {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

// ────────────────────────────────────────────────────────────────
// Segmentierte Stunden-Berechnung (für History-Splitting)
// ────────────────────────────────────────────────────────────────

/** Berechnungsergebnis pro Segment */
export interface SegmentedPayrollData {
  segment: HistorySegment;
  workedHours: number;     // LA 1000
  holidayHours: number;    // LA 1012
  vacationHours: number;   // LA 1600
  sickHours: number;       // LA 1650
  vacationDays: number;    // LA 8560
  workdays: number;        // Arbeitstage im Segment
}

/**
 * Berechnet die Stunden pro Segment im Abrechnungszeitraum.
 * Jeder Tag wird genau dem Segment zugeordnet, in dessen from/to-Bereich er fällt.
 */
export function calculateSegmentedHours(
  entries: Record<string, DayEntry>,
  segments: HistorySegment[],
  period: PayrollPeriod,
  settings: CompanySettings,
): SegmentedPayrollData[] {
  return segments.map((segment) => {
    let workedHours = 0;
    let holidayHours = 0;
    let vacationHours = 0;
    let sickHours = 0;
    let vacationDays = 0;
    let workdays = 0;

    // Effektiver Bereich: Schnittmenge von Segment und Abrechnungszeitraum
    const segStart = segment.from > format(period.start, 'yyyy-MM-dd') ? segment.from : format(period.start, 'yyyy-MM-dd');
    const segEnd = segment.to < format(period.end, 'yyyy-MM-dd') ? segment.to : format(period.end, 'yyyy-MM-dd');

    const current = new Date(segStart + 'T12:00:00');
    const endDate = new Date(segEnd + 'T12:00:00');

    while (current <= endDate) {
      const dateStr = format(current, 'yyyy-MM-dd');
      const entry = entries[dateStr];
      const dayOfWeek = current.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      if (!isWeekend) {
        if (entry) {
          if (entry.isSickDay) {
            sickHours += entry.hours;
          } else if (entry.isVacation) {
            vacationHours += entry.hours;
            vacationDays++;
          } else if (entry.isHalfVacation) {
            vacationHours += entry.hours;
            vacationDays += 0.5;
          } else if (entry.isHoliday) {
            holidayHours += settings.hoursPerDayFullTime;
          } else if (entry.hours > 0) {
            workedHours += entry.hours;
          }
        }
        // Werktag ohne Feiertag → zählt als Arbeitstag
        if (!entry?.isHoliday) {
          workdays++;
        }
      }

      current.setDate(current.getDate() + 1);
    }

    return { segment, workedHours, holidayHours, vacationHours, sickHours, vacationDays, workdays };
  });
}

/**
 * Formatiert den Zeitraum eines Segments als DD.MM.–DD.MM. (nur bei Splits)
 */
function formatSegmentDateRange(segment: HistorySegment): string {
  const fromParts = segment.from.split('-');
  const toParts = segment.to.split('-');
  return `${fromParts[2]}.${fromParts[1]}.–${toParts[2]}.${toParts[1]}.`;
}

/**
 * Erstellt ein Fallback-Segment aus den User-Daten (für User ohne History).
 */
export function createFallbackSegment(
  emp: NewExportEmployeeInput,
  period: PayrollPeriod,
): HistorySegment {
  return {
    from: format(period.start, 'yyyy-MM-dd'),
    to: format(period.end, 'yyyy-MM-dd'),
    values: {
      employmentType: emp.employmentType ?? null,
      wageType: emp.wageType ?? null,
      hourlyWage: emp.settings.hourlyWage ?? null,
      monthlySalary: emp.monthlySalary ?? null,
      contractualHours: emp.contractualHours ?? null,
      department: emp.department ?? null,
      rv: emp.rv ?? false,
      hansefit: emp.hansefit ?? false,
      federalState: emp.settings.federalState ?? 'NI',
      vacationDaysFullTime: emp.settings.vacationDaysFullTime ?? 30,
      workDaysPerWeekFullTime: emp.settings.workDaysPerWeekFullTime ?? 5,
      hoursPerDayFullTime: emp.settings.hoursPerDayFullTime ?? 8,
    },
  };
}

/** CSV-Feld: Werte mit Semikolon oder Anführungszeichen in Anführungszeichen setzen */
function csvField(value: string): string {
  if (value.includes(';') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Export 1: Lohnbüro-Export
 * 7 Spalten: Personalnummer, Name/Vorname, Zeitraum, Bemerkungen, Lohnart, gearbeitete Stunden, Aushilfslohn
 *
 * @param segmentsMap - optionale Segments pro User (aus Batch-Endpoint). Wenn vorhanden und ein User
 *   mehrere Segments hat, wird pro Segment eine Zeile erzeugt. Ohne segmentsMap: eine Zeile wie bisher.
 */
export function exportLohnbueroCSV(
  employees: NewExportEmployeeInput[],
  year: number,
  month: number,
  segmentsMap?: Record<string, HistorySegment[]>,
): void {
  const period = getPayrollPeriod(year, month);

  const header = [
    'Personalnummer',
    'Name, Vorname',
    'Zeitraum',
    'Bemerkungen/Sonstiges',
    'Lohnart',
    'gearbeitete Stunden',
    'Aushilfslohn',
  ].join(';');

  const rows: string[] = [];

  const sorted = [...employees].sort(
    (a, b) => (a.lastName ?? '').localeCompare(b.lastName ?? '', 'de') || (a.firstName ?? '').localeCompare(b.firstName ?? '', 'de')
  );

  for (const emp of sorted) {
    const segments = segmentsMap?.[emp.userId];
    const effectiveSegments = segments && segments.length > 0
      ? segments
      : [createFallbackSegment(emp, period)];

    const segData = calculateSegmentedHours(emp.entries, effectiveSegments, period, emp.settings);
    const hasMultiple = segData.length > 1;

    for (const sd of segData) {
      const billable = sd.workedHours + sd.vacationHours + sd.sickHours;
      const wt = sd.segment.values.wageType;
      const lohnartLabel = wt ? (WAGE_TYPE_LABELS[wt as keyof typeof WAGE_TYPE_LABELS] ?? '') : '';
      const aushilfslohn = wt === 'HOURLY' && sd.segment.values.hourlyWage != null
        ? formatDE(sd.segment.values.hourlyWage)
        : '';

      rows.push([
        csvField(emp.personnelNumber ?? ''),
        csvField(`${emp.lastName ?? ''}, ${emp.firstName ?? ''}`),
        hasMultiple ? formatSegmentDateRange(sd.segment) : '',
        csvField(emp.approval?.approvalNote ?? ''),
        csvField(lohnartLabel),
        formatDE(billable),
        aushilfslohn,
      ].join(';'));
    }
  }

  const monthLabel = (SHORT_MONTHS[month - 1] ?? '').replace('.', '');
  const yearStr = String(year);
  const filename = `Lohnbuero_${monthLabel}_${yearStr}.csv`;
  const content = BOM + header + '\n' + rows.join('\n');
  downloadFile(content, filename, 'text/csv;charset=utf-8;');
}

/**
 * Export 2: Geschäftsführungs-Export
 * 16 Spalten (+ optionale Zeitraum-Spalte bei Splits)
 *
 * @param segmentsMap - optionale Segments pro User (aus Batch-Endpoint)
 */
export function exportGFCSV(
  employees: NewExportEmployeeInput[],
  year: number,
  month: number,
  segmentsMap?: Record<string, HistorySegment[]>,
): void {
  const period = getPayrollPeriod(year, month);

  const header = [
    'Abrechnungsmonat',
    'Nachname',
    'Vorname',
    'Zeitraum',
    'Abteilung/Team',
    'Art',
    'Vertragliche Stunden',
    'Geleistete Stunden',
    'Vertragsbeginn',
    'Vertragsende',
    'Arbeitstage aktueller Monat',
    'Vertraglicher Lohn',
    'RV',
    'Hansefit',
    'Lohn aktueller Monat',
    'Anmerkung zur Freigabe',
  ].join(';');

  const abrechnungsmonat = formatShortMonth(month, year);
  const rows: string[] = [];

  const sorted = [...employees].sort(
    (a, b) => (a.lastName ?? '').localeCompare(b.lastName ?? '', 'de') || (a.firstName ?? '').localeCompare(b.firstName ?? '', 'de')
  );

  for (const emp of sorted) {
    const segments = segmentsMap?.[emp.userId];
    const effectiveSegments = segments && segments.length > 0
      ? segments
      : [createFallbackSegment(emp, period)];

    const segData = calculateSegmentedHours(emp.entries, effectiveSegments, period, emp.settings);
    const hasMultiple = segData.length > 1;

    for (const sd of segData) {
      const v = sd.segment.values;
      const billable = sd.workedHours + sd.vacationHours + sd.sickHours;
      const empTypeLabel = v.employmentType ? (EMPLOYMENT_TYPE_LABELS[v.employmentType] ?? '') : '';

      let lohnAktuell = '';
      if (v.wageType === 'FIXED' && v.monthlySalary != null) {
        lohnAktuell = formatDE(v.monthlySalary);
      } else if (v.wageType === 'HOURLY' && v.hourlyWage != null) {
        lohnAktuell = formatDE(billable * v.hourlyWage);
      }

      rows.push([
        csvField(abrechnungsmonat),
        csvField(emp.lastName ?? ''),
        csvField(emp.firstName ?? ''),
        hasMultiple ? formatSegmentDateRange(sd.segment) : '',
        csvField(v.department ?? ''),
        csvField(empTypeLabel),
        v.wageType === 'HOURLY' && v.hourlyWage != null
          ? formatDE(v.hourlyWage)
          : (v.contractualHours != null ? formatDE(v.contractualHours) : ''),
        formatDE(billable),
        formatDateDE(emp.employmentStart),
        formatDateDE(emp.employmentEnd),
        sd.workdays.toString(),
        v.monthlySalary != null ? formatDE(v.monthlySalary) : '',
        v.rv ? 'ja' : 'nein',
        v.hansefit ? 'ja' : 'nein',
        lohnAktuell,
        csvField(emp.approval?.approvalNote ?? ''),
      ].join(';'));
    }
  }

  const monthLabel = (SHORT_MONTHS[month - 1] ?? '').replace('.', '');
  const yearStr = String(year);
  const filename = `GF_Export_${monthLabel}_${yearStr}.csv`;
  const content = BOM + header + '\n' + rows.join('\n');
  downloadFile(content, filename, 'text/csv;charset=utf-8;');
}

// ────────────────────────────────────────────────────────────────
// Hilfsfunktion
// ────────────────────────────────────────────────────────────────

function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}
